import React, { useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import {
  Box,
  Chip,
  IconButton,
  InputBase,
  Paper,
  Switch,
  Typography,
} from "@mui/material"
import { CloseOutlined, SearchOutlined } from "@mui/icons-material"

import { parseDate, sectionValue } from "./utils"
import useCypeSearch from "./useCypeSearch"
import mapToPartida from "./mapToPartida"
import PartBrowserFilters from "./PartBrowserFilters"
import PartBrowserTable from "./PartBrowserTable"
import PartBrowserDetail from "./PartBrowserDetail"

const DIVIDER = "rgba(0,0,0,0.12)"
const MUTED = "rgba(0,0,0,0.6)"

const PartBrowser = ({ mode, onAddPart, onBusyChange }) => {
  const isModal = mode === "modal"
  const [addedIds, setAddedIds] = useState([])
  const [addingId, setAddingId] = useState(null)
  const [bankSavedIds, setBankSavedIds] = useState([])

  const [query, setQuery] = useState("")
  const [showFilters, setShowFilters] = useState(false)
  const [onlyFavorites, setOnlyFavorites] = useState(false)
  const [sourceFilter, setSourceFilter] = useState("")
  const [unitFilter, setUnitFilter] = useState("")
  const [minPrice, setMinPrice] = useState("")
  const [maxPrice, setMaxPrice] = useState("")
  const [sortBy, setSortBy] = useState("relevance")
  const [selectedId, setSelectedId] = useState(null)
  const [detailTab, setDetailTab] = useState("summary")
  const [detailVisible, setDetailVisible] = useState(false)
  const [favorites, setFavorites] = useState({})

  const sectionFilter = sectionValue(sourceFilter)

  const {
    items: rawItems,
    loading,
    loadingMore,
    total,
    error,
    units,
    hasMore,
    fetchMore,
    fetchDetail,
  } = useCypeSearch(mode, query, sectionFilter, unitFilter, minPrice, maxPrice)

  const items = useMemo(
    () => rawItems.map(item => ({ ...item, favorite: !!favorites[item.id] })),
    [rawItems, favorites],
  )

  useEffect(() => {
    if (onBusyChange) onBusyChange(!!addingId)
  }, [addingId, onBusyChange])

  const filtered = useMemo(() => {
    let data = [...items]
    if (onlyFavorites) data = data.filter(item => item.favorite)
    if (sortBy === "price") data.sort((a, b) => a.unitPrice - b.unitPrice)
    if (sortBy === "updated")
      data.sort((a, b) => parseDate(b.updatedAt) - parseDate(a.updatedAt))
    if (sortBy === "code") data.sort((a, b) => a.code.localeCompare(b.code))
    return data
  }, [items, onlyFavorites, sortBy])

  const selected =
    filtered.find(item => item.id === selectedId) || filtered[0] || null

  // La fila seleccionada puede no venir de un clic: `selected` cae en `filtered[0]` tras
  // cada búsqueda, y ese camino no pasa por `onSelectRow`. Sin esto la primera partida se
  // mostraría sin desglose ni ubicación (sólo llegan en /cype/item) hasta hacer clic.
  // Se registran los ids ya pedidos en vez de mirar si el dato llegó: una respuesta sin
  // `chapter_path` volvería a cumplir la condición y dispararía peticiones en bucle.
  // El registro se vacía al cambiar la búsqueda, porque `setItems` reemplaza los items y
  // el detalle hay que volver a pedirlo aunque el id repita.
  const detailFetchedRef = useRef({ signature: null, ids: new Set() })
  useEffect(() => {
    if (!selected) return
    const signature = `${query}|${sectionFilter || ""}|${unitFilter || ""}|${minPrice}|${maxPrice}`
    const state = detailFetchedRef.current
    if (state.signature !== signature) {
      state.signature = signature
      state.ids = new Set()
    }
    if (state.ids.has(selected.id)) return
    state.ids.add(selected.id)
    // Si falla, se desmarca para poder reintentar en un render futuro con el
    // mismo `selected` (p. ej. tras un 503 de throttling); si no se hiciera,
    // esa partida se quedaría sin breakdown/ubicación para siempre en esta
    // búsqueda. El efecto corre en cada render (sin deps array) a propósito:
    // es lo que permite que este reintento se dispare en el siguiente render
    // que sea, sin depender de qué lo cause.
    fetchDetail(selected.id).then(detail => {
      if (!detail) state.ids.delete(selected.id)
    })
  })

  const updateQuery = value => {
    setQuery(value)
  }

  const resetFilters = () => {
    setQuery("")
    setSourceFilter("")
    setUnitFilter("")
    setMinPrice("")
    setMaxPrice("")
    setOnlyFavorites(false)
  }

  const handleSourceChange = value => {
    setSourceFilter(value)
  }

  const handleUnitChange = value => {
    setUnitFilter(value)
  }

  const handleAddItem = async item => {
    if (!item || addingId || !onAddPart) return

    setAddingId(item.id)
    try {
      // El desglose solo llega con /cype/item, y el listado de búsqueda no lo trae.
      // Sin esto la partida se crearía sin descomposición si el usuario añade antes
      // de que el detalle haya terminado de cargar.
      let full = item
      if (item.hasBreakdown && !(item.breakdown || []).length) {
        const detail = await fetchDetail(item.id)
        if (detail) full = { ...item, breakdown: detail.breakdown || [] }
      }
      setAddedIds(prev => (prev.includes(item.id) ? prev : [...prev, item.id]))
      onAddPart(mapToPartida(full))
    } finally {
      setAddingId(null)
    }
  }

  const handleSaveToBank = item => {
    if (!item) return
    setBankSavedIds(prev =>
      prev.includes(item.id) ? prev : [...prev, item.id],
    )
  }

  const toggleFavorite = itemId => {
    setFavorites(prev => ({ ...prev, [itemId]: !prev[itemId] }))
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        minHeight: 600,
      }}
    >
      {/* Single toolbar: search + controls */}
      <Box
        sx={{
          px: 3,
          py: 1.25,
          borderBottom: `1px solid ${DIVIDER}`,
          bgcolor: "#fafafa",
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 800,
            color: "#414141",
            fontFamily: "Helvetica",
            flexShrink: 0,
          }}
        >
          {i18n.t("partBrowser.title")}
        </Typography>
        {/* Search — takes all available space */}
        <Paper
          variant="outlined"
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.625,
            borderRadius: 3,
            bgcolor: "#fff",
            "&:focus-within": { borderColor: "#222" },
          }}
        >
          <SearchOutlined sx={{ color: MUTED, fontSize: 18 }} />
          <InputBase
            value={query}
            onChange={e => updateQuery(e.target.value)}
            placeholder={i18n.t("partBrowser.searchPlaceholder")}
            sx={{ flex: 1, fontSize: 14 }}
          />
          {query && (
            <IconButton
              size="small"
              onClick={() => updateQuery("")}
              sx={{ p: 0.25 }}
            >
              <CloseOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Paper>

        {/* Favorites switch */}
        {/* OCULTO — pendiente de backend: los favoritos NO están implementados.
            `toggleFavorite` sólo hace setState de `favorites` (ver abajo): no persiste en
            ningún sitio y se pierde al refrescar. Está oculta toda la funcionalidad —
            este filtro, el ⭐ del panel de detalle y el item del menú de fila — porque
            marcar favoritas sin persistencia no sirve de nada.
            Además, incluso persistiendo en cliente, el filtro sólo afectaría a las filas
            ya cargadas (50 por página), no al total: con paginación por cursor hay que
            filtrar server-side.
            Para reactivar: endpoint de favoritos en el backend de planhopper (con auth),
            guardando el id del catálogo (p.ej. "HRP020-82e6531c"). No va en scrap-cype:
            es catálogo de referencia read-only, sin tenant y con caché pública. */}
        <Box
          sx={{
            display: "none",
            alignItems: "center",
            gap: 0.75,
            cursor: "pointer",
            flexShrink: 0,
          }}
          onClick={() => setOnlyFavorites(v => !v)}
        >
          <Switch
            checked={onlyFavorites}
            size="small"
            sx={{
              "& .MuiSwitch-thumb": {
                bgcolor: onlyFavorites ? "#ffd52b" : undefined,
              },
            }}
          />
          <Typography sx={{ fontSize: 13, whiteSpace: "nowrap" }}>
            Favoritas
          </Typography>
        </Box>

        {/* Sort */}
        {/* OCULTO — pendiente de backend: `GET /cype/search` no acepta parámetro `sort`
            (verificado: sort=relevance, sort=price y sin sort devuelven el mismo orden).
            El backend ordena siempre por `updatedAt DESC, id ASC`, y "Mejor coincidencia"
            nunca existió: no hay rama para 'relevance' en `filtered` más abajo, y el
            `_score` de OpenSearch se calcula pero se descarta.
            Los otros tres (código/precio/actualización) sí ordenan, pero sólo las filas
            ya cargadas, no el total (p.ej. 19.001 resultados para "hormigon"), por lo que
            son incorrectos con paginación por cursor.
            Para reactivar: implementar `?sort=relevance|price|code|updated` en scrap-cype
            (el cursor debe codificar la clave del sort activo) y ordenar server-side. */}
        <Box sx={{ display: "none", flexShrink: 0 }}>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            style={{
              fontSize: 13,
              padding: "6px 8px",
              border: `1px solid ${DIVIDER}`,
              borderRadius: 8,
              background: "#fff",
              flexShrink: 0,
              width: "fit-content",
            }}
          >
            <option value="relevance">Mejor coincidencia</option>
            <option value="code">Código</option>
            <option value="price">Precio</option>
            <option value="updated">Actualización</option>
          </select>
        </Box>

        {/* Results count */}
        <Chip
          label={`${total}`}
          size="small"
          sx={{
            height: 28,
            fontWeight: 700,
            bgcolor: "#222",
            color: "#fff",
            flexShrink: 0,
            mr: isModal ? 4.5 : 0,
          }}
        />
      </Box>

      {/* Main content: filters | table | detail */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>
        {/* Sidebar */}
        <PartBrowserFilters
          source={sourceFilter}
          onSourceChange={handleSourceChange}
          unit={unitFilter}
          units={units}
          onUnitChange={handleUnitChange}
          minPrice={minPrice}
          maxPrice={maxPrice}
          onMinPriceChange={setMinPrice}
          onMaxPriceChange={setMaxPrice}
          onReset={resetFilters}
          onHide={() => setShowFilters(false)}
          collapsed={!showFilters}
          onShow={() => setShowFilters(true)}
        />

        {/* Center: table */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <PartBrowserTable
            rows={filtered}
            selectedId={selectedId}
            query={query}
            loading={loading}
            error={error}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={fetchMore}
            onSelectRow={id => {
              setSelectedId(id)
              setDetailVisible(true)
            }}
            onResetFilters={resetFilters}
          />
        </Box>

        {/* Detail panel */}
        <PartBrowserDetail
          selected={selected}
          canAdd={!!onAddPart}
          addedIds={addedIds}
          addingId={addingId}
          bankSavedIds={bankSavedIds}
          tab={detailTab}
          onTabChange={setDetailTab}
          onPrimaryAction={handleAddItem}
          onSaveToBank={handleSaveToBank}
          onToggleFavorite={toggleFavorite}
          onHide={() => setDetailVisible(false)}
          collapsed={!detailVisible}
          onShow={() => setDetailVisible(true)}
        />
      </Box>
    </Box>
  )
}

PartBrowser.propTypes = {
  mode: PropTypes.oneOf(["page", "modal"]),
  onAddPart: PropTypes.func,
  onBusyChange: PropTypes.func,
}

PartBrowser.defaultProps = {
  mode: "page",
  onAddPart: null,
  onBusyChange: null,
}

export default PartBrowser
